/**
 * ContaSaldoSection — «Conta e saldo» do domínio IA & conduta (IA como serviço S2, Task 14).
 *
 * Responde três perguntas, em ordem: **quanto o tenant tem para gastar**, **a que preço**, e
 * **para onde cada real foi**. Somente leitura — recarga e ajuste nascem no console da
 * plataforma (`admin-lume`, S5). Não há «Sincronizar agora» (S3) nem «Usar minha própria
 * chave» (S4): ausências DELIBERADAS, não esquecimento.
 *
 * Gate de leitura: NÃO tem um próprio. Quem gateia é o `DomainSection` do hub pela
 * `capability` do item de catálogo (`ai-billing-read`) — mesmo contrato que `ModelosSection`
 * segue desde a T8 (gate duplicado foi removido de lá). O único gate interno é o do botão de
 * exportar (`ai-billing-manage`): exportar o livro-razão para fora do produto é ato
 * administrativo, e o engine recusa a quem não tem a cap.
 *
 * Decisões que valem registro:
 *
 *  1. **O CSV baixa por Blob autenticado, não por `<a href>`.** O brief pedia «link direto à
 *     URL», mas `rawFetch` (api-client) manda `Authorization` e `X-Tenant-Slug` em HEADER —
 *     este app não tem cookie de sessão. Uma navegação direta à URL chegaria ao engine sem
 *     credencial e voltaria **401**, com o navegador mostrando uma página de erro no lugar do
 *     arquivo. O molde usado é o que o repo já tem em dois lugares (`exportAuditEvents` em
 *     `AuditExplorerPage.tsx`, `exportTeamPendenciesCsv`). O `aiWalletCsvUrl` que o plano
 *     listava **não existe**: seria uma função exportada sem chamador, e o único uso natural
 *     dela (montar um `href`) é justamente o que dá 401. A janela do mês é asserida no teste
 *     sobre os argumentos de `exportAiWalletCsv`.
 *
 *  2. **A tabela mostra o preço EFETIVO com a ORIGEM de cada linha (GX1).** O endpoint já
 *     resolve as duas camadas (negociação do tenant → tabela da Lume) e devolve uma linha por
 *     modelo, só as vigentes. A coluna «Origem» («Negociado» × «Tabela Lume») é o que deixa o
 *     operador saber por que dois tenants pagam diferente sem abrir chamado. Histórico de preço
 *     é assunto de outra tela.
 *
 *  3. **`consumedPct` pode passar de 100** (saldo negativo: o débito da invocação em curso
 *     não é recusado pela metade). A barra satura em 100% no desenho, mas o número em texto
 *     mostra o valor real — saturar o texto esconderia justamente o caso que importa.
 *
 *  4. **`min-w-0` em todo filho de flex que carrega texto longo** — sem ele o `truncate` não
 *     encolhe e o conteúdo vaza por cima do painel vizinho (memória
 *     `reference_min_content_vaza_e_sobrepoe_painel`).
 *
 *  5. **Cor semântica vem de token em `style`, não de classe Tailwind.** O tema deste app
 *     (`tailwind.config.ts`) declara só 8 cores — `surface`, `text-primary`, `text-secondary`,
 *     `border-subtle`, `accent`… — e NÃO tem `danger-fg`, `success-fg` nem `bg-muted`. Uma
 *     classe `text-danger-fg` compila para nada: o saldo negativo sairia da mesma cor do
 *     positivo, sem erro nenhum a apontar isso (o repo já carrega 15 usos inertes de
 *     `hover:bg-bg-muted` em `features/admin/` por esta mesma razão — pré-existentes, não
 *     tocados aqui). Os tokens `--danger-fg`/`--success-fg`/`--bg-muted` existem em
 *     `packages/design-system/src/tokens.css`, com valor próprio no tema escuro, e entram por
 *     `style={{ color: 'var(--danger-fg)' }}` — o mesmo molde de `ChecklistBlock.tsx`.
 */
import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { api } from '../../../lib/api-client';

import { useAiWallet, useAiWalletContract, useAiWalletEntries } from './use-ai-wallet';

import type { WalletEntryWire, WalletMode } from './types';

const MODO_LABEL: Record<WalletMode, string> = {
  platform_prepaid: 'Pela Lume (pré-pago)',
  byok: 'Chave própria',
};

const KIND_LABEL: Record<string, string> = {
  credit: 'Crédito',
  debit: 'Débito',
  adjustment: 'Ajuste',
};

const SOURCE_LABEL: Record<string, string> = {
  console_manual: 'Recarga manual',
  superger_sale: 'Venda SuperGer',
  adjustment: 'Correção',
  ai_invocation: 'Uso de IA',
};

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const DATA_HORA = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
const DATA = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

/**
 * `Intl` pt-BR separa «R$» do número com NBSP (U+00A0) e, em algumas versões do ICU, com
 * NNBSP (U+202F). Normalizar para espaço comum evita que o texto na tela seja invisivelmente
 * diferente do que qualquer busca (ou teste) procura.
 */
function semNbsp(texto: string): string {
  return texto.replace(/[\u00a0\u202f]/g, ' ');
}

function reais(valor: number): string {
  return semNbsp(BRL.format(valor));
}

function dataHora(iso: string): string {
  return semNbsp(DATA_HORA.format(new Date(iso)));
}

function dataCurta(iso: string): string {
  return semNbsp(DATA.format(new Date(iso)));
}

/** Janela padrão do export: do 1º dia do mês corrente até agora. */
function janelaDoMes(): { since: string; until: string } {
  const agora = new Date();
  const inicio = new Date(agora.getFullYear(), agora.getMonth(), 1, 0, 0, 0, 0);
  return { since: inicio.toISOString(), until: agora.toISOString() };
}

function ValorComSinal({ entry }: { readonly entry: WalletEntryWire }): JSX.Element {
  const positivo = entry.amountBrl >= 0;
  return (
    <span style={{ color: positivo ? 'var(--success-fg)' : 'var(--fg-1)' }}>
      {positivo ? '+' : '−'}
      {reais(Math.abs(entry.amountBrl))}
    </span>
  );
}

export function ContaSaldoSection(): JSX.Element {
  const podeExportar = useCapability('ai-billing-manage');
  const carteira = useAiWallet();
  const contrato = useAiWalletContract();
  const extrato = useAiWalletEntries();
  const [exportando, setExportando] = useState(false);
  const [erroExport, setErroExport] = useState<string | null>(null);

  const snap = carteira.data;
  const pctReal = snap?.consumedPct ?? 0;
  const pctBarra = Math.min(100, Math.max(0, Math.round(pctReal)));
  const linhasVigentes = contrato.data?.lines ?? [];
  const negociacao = contrato.data?.exception ?? null;
  const lancamentos = extrato.data?.pages.flatMap((p) => p.items) ?? [];

  /**
   * O `catch` não é cerimônia: este download NÃO passa por `callJson`, e é `callJson` quem
   * dispara o toast global de 403/erro. Sem ele, um 400 de janela grande demais (o teto de
   * 20.000 lançamentos do engine) ou um 403 de capability sumiriam sem deixar nada na tela —
   * o operador clicaria de novo achando que não clicou.
   */
  async function exportar(): Promise<void> {
    const { since, until } = janelaDoMes();
    setExportando(true);
    setErroExport(null);
    try {
      const blob = await api.exportAiWalletCsv(since, until);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `extrato-ia-${since.slice(0, 10)}-${until.slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErroExport(e instanceof Error ? e.message : 'Não foi possível exportar o extrato.');
    } finally {
      setExportando(false);
    }
  }

  return (
    <div className="flex flex-col">
      {/* ── Cabeçalho: modo, saldo, ciclo ──────────────────────────────── */}
      <div className="rounded-xl border border-border-subtle bg-surface px-8 py-5">
        {carteira.isError && snap === undefined ? (
          // Pelo `isError`, não pela ausência de dado (revisão final, M5c): as duas coisas
          // não são a mesma, e a diferença aparece no refetch falho — ali `snap` existe e o
          // que a tela precisa dizer é «este número é de antes», não «não deu para ler».
          <p role="alert" className="text-sm text-text-secondary">
            Não foi possível ler a carteira agora. Recarregue a página para tentar de novo.
          </p>
        ) : snap === undefined ? (
          <p className="text-sm text-text-secondary">Carregando o saldo…</p>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-text-secondary">
                  Modo de cobrança
                </p>
                <p className="mt-0.5 text-sm font-medium text-text-primary">
                  {MODO_LABEL[snap.mode]}
                </p>
              </div>
              <div className="min-w-0 text-right">
                <p className="text-xs uppercase tracking-wide text-text-secondary">Saldo</p>
                <p
                  data-testid="saldo-atual"
                  className="mt-0.5 text-2xl font-semibold"
                  style={{ color: snap.balanceBrl < 0 ? 'var(--danger-fg)' : 'var(--fg-1)' }}
                >
                  {reais(snap.balanceBrl)}
                </p>
              </div>
            </div>

            {snap.mode === 'byok' && (
              <p className="mt-3 text-sm text-text-secondary">
                Este tenant usa a própria chave de IA: a carteira da Lume não é cobrada pelo uso.
              </p>
            )}

            {/* Barra do ciclo — marcas em 80 e 95, as mesmas que disparam aviso. */}
            <div className="mt-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="min-w-0 text-sm text-text-secondary">
                  {`${pctReal.toFixed(1).replace('.', ',')}% do ciclo consumido`}
                  <span className="text-text-secondary">
                    {` — carga do ciclo: ${reais(snap.cycleCreditedBrl)}`}
                  </span>
                </p>
                {!snap.enforcementEnabled && (
                  // Chip de AVISO, não de tier. O estilo `bg-accent/10 text-accent` é o do
                  // badge «Pro» do `DomainSection` — usá-lo aqui faria um sinal de risco
                  // («a carteira mede e NÃO corta») parecer selo de plano. Tokens
                  // `--warning-*` de `packages/design-system/src/tokens.css`, com valor
                  // próprio no tema escuro, mais o glifo ⚠ para não depender só da cor.
                  <span
                    className="rounded border px-2 py-0.5 text-xs font-medium"
                    style={{
                      color: 'var(--warning-fg)',
                      background: 'var(--warning-bg)',
                      borderColor: 'var(--warning-border)',
                    }}
                  >
                    ⚠ corte desligado
                  </span>
                )}
              </div>
              <div
                role="progressbar"
                aria-label="Consumo do ciclo"
                aria-valuenow={pctBarra}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuetext={`${pctReal.toFixed(1).replace('.', ',')}% consumido`}
                className="relative mt-2 h-2 w-full overflow-hidden rounded-full"
                style={{ background: 'var(--bg-muted)' }}
              >
                <span
                  className="block h-full rounded-full bg-accent"
                  style={{ width: `${String(pctBarra)}%` }}
                />
                <span
                  data-testid="marca-80"
                  aria-hidden="true"
                  className="absolute top-0 h-full w-px bg-border-subtle"
                  style={{ left: '80%' }}
                />
                <span
                  data-testid="marca-95"
                  aria-hidden="true"
                  className="absolute top-0 h-full w-px bg-border-subtle"
                  style={{ left: '95%' }}
                />
              </div>
              {snap.warnedPct > 0 && (
                <p className="mt-1.5 text-xs text-text-secondary">
                  {`Aviso de ${String(snap.warnedPct)}% já enviado neste ciclo.`}
                </p>
              )}
            </div>

            {carteira.isError && (
              // Dado velho ROTULADO — mesmo contrato do extrato e do contrato de preço. Numa
              // tela de dinheiro, um saldo sem rótulo é lido como o saldo de agora.
              <p className="mt-3 text-sm text-text-secondary">
                Não foi possível atualizar o saldo agora — o número acima é a última leitura que deu
                certo.
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Contrato de preço vigente ──────────────────────────────────── */}
      <div className="mt-6">
        <h3 className="text-sm font-medium text-text-primary">Preço vigente</h3>
        {contrato.isLoading ? (
          <p className="mt-2 text-sm text-text-secondary">Carregando o contrato…</p>
        ) : contrato.isError && contrato.data === undefined ? (
          // ANTES do caso vazio, de propósito: numa tela de dinheiro, uma query que FALHOU
          // não pode render «não há contrato» — isso afirma um fato que ninguém mediu, e o
          // tenant concluiria que perdeu o contrato quando o que caiu foi a rede. O
          // `data === undefined` mantém a tabela em pé quando o que falhou foi um REFETCH
          // (o react-query preserva o dado anterior): dado velho ROTULADO (logo abaixo da
          // tabela, como no extrato) é melhor que tela em branco.
          <p role="alert" className="mt-2 text-sm text-text-secondary">
            Não foi possível ler o contrato agora. Recarregue a página para tentar de novo.
          </p>
        ) : linhasVigentes.length === 0 ? (
          <p className="mt-2 text-sm text-text-secondary">
            Nenhum preço vigente para este tenant. Fale com a Lume.
          </p>
        ) : (
          <>
            {negociacao !== null && (
              <p className="mt-1 text-sm text-text-secondary">
                Negociação vigente: <span className="text-text-primary">{negociacao.label}</span>{' '}
                desde {dataCurta(negociacao.startsAt)}
                {negociacao.notes !== null && negociacao.notes !== ''
                  ? ` — ${negociacao.notes}`
                  : ''}
              </p>
            )}
            <div className="mt-2 overflow-x-auto rounded-xl border border-border-subtle bg-surface">
              <table className="w-full text-sm" aria-label="Contrato de preço vigente">
                <thead>
                  <tr className="border-b border-border-subtle text-left text-xs uppercase tracking-wide text-text-secondary">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Modelo
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Entrada (R$/Mtok)
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Saída (R$/Mtok)
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Desde
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Origem
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {linhasVigentes.map((l) => (
                    <tr
                      key={`${l.modelId}-${l.validFrom}`}
                      className="border-b border-border-subtle"
                    >
                      <td className="max-w-xs truncate px-4 py-2 text-text-primary">{l.modelId}</td>
                      <td className="px-4 py-2 text-text-primary">{reais(l.inputBrlPerMtok)}</td>
                      <td className="px-4 py-2 text-text-primary">{reais(l.outputBrlPerMtok)}</td>
                      <td className="px-4 py-2 text-text-secondary">{dataCurta(l.validFrom)}</td>
                      <td className="px-4 py-2 text-text-secondary">
                        {l.layer === 'tenant' ? 'Negociado' : 'Tabela Lume'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {contrato.isError && (
              // A promessa do comentário acima, cumprida: o dado velho fica, mas ROTULADO.
              // Sem isto, um refetch falho mostrava a tabela de preço como se fosse a
              // leitura de agora — e preço é a base de tudo que se cobra nesta tela.
              <p className="mt-2 text-sm text-text-secondary">
                Não foi possível atualizar o contrato agora — o que está acima é a última leitura
                que deu certo.
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Extrato ────────────────────────────────────────────────────── */}
      <div className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="min-w-0 text-sm font-medium text-text-primary">Extrato</h3>
          {podeExportar && (
            <button
              type="button"
              onClick={() => void exportar()}
              disabled={exportando}
              className="rounded-md border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-app-bg disabled:opacity-50"
            >
              {exportando ? 'Exportando…' : 'Exportar extrato (CSV)'}
            </button>
          )}
        </div>
        {erroExport !== null && (
          <p role="alert" className="mt-2 text-sm" style={{ color: 'var(--danger-fg)' }}>
            {`Não foi possível exportar o extrato: ${erroExport}`}
          </p>
        )}

        {extrato.isLoading ? (
          <p className="mt-2 text-sm text-text-secondary">Carregando o extrato…</p>
        ) : extrato.isError && lancamentos.length === 0 ? (
          // Mesma regra do contrato acima: extrato que não carregou ≠ carteira sem lançamento.
          // Com páginas já carregadas, a tabela FICA e o aviso de falha vai abaixo dela —
          // sumir com o extrato inteiro porque a 2ª página caiu seria pior que o problema.
          <p role="alert" className="mt-2 text-sm text-text-secondary">
            Não foi possível ler o extrato agora. Recarregue a página para tentar de novo.
          </p>
        ) : lancamentos.length === 0 ? (
          <p className="mt-2 text-sm text-text-secondary">
            Nenhum lançamento ainda — nada foi creditado nem consumido nesta carteira.
          </p>
        ) : (
          <>
            <div className="mt-2 overflow-x-auto rounded-xl border border-border-subtle bg-surface">
              <table className="w-full text-sm" aria-label="Extrato da carteira">
                <thead>
                  <tr className="border-b border-border-subtle text-left text-xs uppercase tracking-wide text-text-secondary">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Data
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Movimento
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Referência
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Valor
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Saldo depois
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lancamentos.map((e) => (
                    <tr key={e.id} className="border-b border-border-subtle">
                      <td className="whitespace-nowrap px-4 py-2 text-text-secondary">
                        {dataHora(e.createdAt)}
                      </td>
                      <td className="px-4 py-2 text-text-primary">
                        {`${KIND_LABEL[e.kind] ?? e.kind} · ${SOURCE_LABEL[e.source] ?? e.source}`}
                      </td>
                      <td className="max-w-xs truncate px-4 py-2 text-text-secondary">
                        {e.reference}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2">
                        <ValorComSinal entry={e} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-text-secondary">
                        {reais(e.balanceAfterBrl)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {extrato.isError && (
              <p className="mt-2 text-sm text-text-secondary">
                Não foi possível ler o restante do extrato agora — o que está acima já foi
                carregado.
              </p>
            )}
            {extrato.hasNextPage && (
              <button
                type="button"
                onClick={() => void extrato.fetchNextPage()}
                disabled={extrato.isFetchingNextPage}
                className="mt-3 rounded-md border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-app-bg disabled:opacity-50"
              >
                {extrato.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
